import { useState } from 'react';
import { Calendar, Save, CheckCircle2 } from 'lucide-react';
import { useStore } from '@/store/StoreContext';
import { Card, SectionTitle, EmptyState, Button, TextArea } from '@/components/ui';

export function DailyProgressView() {
  const { activeClass, activeClassAgents, getDailyLog, upsertDailyLog, userRole } = useStore();
  const [selectedAgentId, setSelectedAgentId] = useState(activeClassAgents[0]?.id || '');
  const [selectedDay, setSelectedDay] = useState('Day 1');
  const [challenges, setChallenges] = useState('');
  const [achievements, setAchievements] = useState('');
  const [saved, setSaved] = useState(false);

  const selectedAgent = activeClassAgents.find((a) => a.id === selectedAgentId);

  const handleSave = () => {
    if (userRole === 'viewer' || userRole === 'guest') {
      alert('Viewers have read-only access.');
      return;
    }
    if (!selectedAgentId) return;

    upsertDailyLog({
      agentId: selectedAgentId,
      weekDay: selectedDay,
      challenges,
      achievements,
    });

    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <SectionTitle
        title="Daily Progress Tracking"
        subtitle="Log daily achievements, challenges, and notes for individual trainees."
        icon={<Calendar className="w-6 h-6" />}
      />

      {!activeClass || activeClassAgents.length === 0 ? (
        <Card className="p-12 text-center text-slate-500">
          <EmptyState message="No active class or trainees found. Please add trainees to the roster first." />
        </Card>
      ) : (
        <Card className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Select Trainee</label>
              <select
                value={selectedAgentId}
                onChange={(e) => setSelectedAgentId(e.target.value)}
                className="w-full rounded-xl border border-slate-300 px-3.5 py-2.5 text-sm text-slate-900 bg-white font-medium focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
              >
                {activeClassAgents.map((agent) => (
                  <option key={agent.id} value={agent.id}>
                    {agent.name} ({agent.pNumber})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Training Day</label>
              <select
                value={selectedDay}
                onChange={(e) => setSelectedDay(e.target.value)}
                className="w-full rounded-xl border border-slate-300 px-3.5 py-2.5 text-sm text-slate-900 bg-white font-medium focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
              >
                {Array.from({ length: 18 }, (_, i) => (
                  <option key={i} value={i < 15 ? `Day ${i + 1}` : `Hypercare ${i - 14}`}>
                    {i < 15 ? `Day ${i + 1}` : `Hypercare Day ${i - 14}`}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="space-y-4 pt-2 border-t border-slate-100">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">Key Achievements & Learning Milestones</label>
              <TextArea
                value={achievements}
                onChange={setAchievements}
                placeholder="Logged successes or topics mastered today..."
                rows={3}
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">Challenges & Required Support</label>
              <TextArea
                value={challenges}
                onChange={setChallenges}
                placeholder="Areas requiring extra coaching or system support..."
                rows={3}
              />
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
            {saved && (
              <span className="flex items-center gap-1.5 text-xs font-semibold text-emerald-600 animate-fade-in">
                <CheckCircle2 className="h-4 w-4" /> Daily log saved!
              </span>
            )}
            <Button onClick={handleSave}>
              <Save className="h-4 w-4" /> Save Daily Progress
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
